import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Tooltip, Popconfirm } from 'antd';
import {
  CommissionAdd,
  CommissionBonus,
  Time,
  FlatFee,
  NumBookings,
  TenancyLength,
  RebookersCommission,
} from 'common/components/svgs';
import { getSymbolFromCurrency } from '~helpers/currency';
import {
  commissionStatus,
  PreCalCommissionWhenPropertyTierChangeAction,
} from '~constants/commission';
import * as commissionActions from '~actions/properties/commission';
import showElementByAuth from '~helpers/auth';
import { platformEntity, entityAction, commissionLevel as commissionLevelConst } from '~constants';
import { dayFormatType, formatDay, moment } from '~helpers/day';
import { InfoCircleOutlined } from '@ant-design/icons';
import ReviewCommission from '../review-commission/';

const BookingJourneyStatus = {
  SEMI_AUTOMATIC: 'Book',
  FULL_AUTOMATIC: 'Connect(Student Suite)',
  INSTANT_BOOK: 'Connect(Student.Com)',
  LEAD_GENERATION: 'List',
};
const mapDispatchToProps = dispatch => ({
  deleteCommission: (input, callback) => {
    dispatch(commissionActions.deleteCommission(input, callback));
  },
});
@connect(null, mapDispatchToProps)
export default class CommissionList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCard: 3,
      selectedReviewCommission: null,
      showDeletePopConfirm: false,
    };
    this.timeDom = [];
  }

  componentDidMount() {
    this.showCardNumber();
    window.addEventListener('resize', this.showCardNumber);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.showCardNumber);
  }

  showCardNumber = () => {
    const dashboardLayout = document.querySelector('.dashboard__layout');
    const dashboardLayoutWidth = dashboardLayout ? dashboardLayout.clientWidth : 0;
    if (dashboardLayoutWidth >= 1250) {
      this.setState({
        showCard: 3,
      });
    }

    if (dashboardLayoutWidth < 1250 && dashboardLayoutWidth > 724) {
      this.setState({
        showCard: 2,
      });
    }
  };

  showUpdate = updatedAt => (
    <React.Fragment>
      <span>{this.props.t('cms.property.commission.list.updated_time')}:&nbsp;</span>
      <span className="commission-list__update-time">
        {formatDay(dayFormatType.UKWITHHMS, updatedAt)}
      </span>
    </React.Fragment>
  );

  sortCommission = commission => {
    if (commission.length === 0) {
      return [];
    }

    const statusSortArr = [
      commissionStatus.ACTIVE,
      commissionStatus.INACTIVE,
      commissionStatus.EXPIRE,
    ];

    return [...commission].sort((a, b) => {
      if (a.status === b.status) {
        return moment(a.updatedAt).isBefore(moment(b.updatedAt));
      }

      return statusSortArr.indexOf(a.status) - statusSortArr.indexOf(b.status);
    });
  };

  handleDeleteCommission = id => {
    this.props.deleteCommission({ id }, this.props.afterDeleteCommission);
  };
  commissionCurrency = currencyValues => {
    let temp = [];
    if (currencyValues && Object.keys(currencyValues).length > 0) {
      temp =
        Object.keys(currencyValues).map(key => ({
          key: key?.toUpperCase(),
          value: currencyValues[key],
        })) || [];
    }
    return temp;
  };

  firstCurrency = currencies => {
    const validCurrencies = currencies.filter(item => item.value);
    return validCurrencies.length > 0 ? validCurrencies[0] : null;
  };

  displayCurrency = commissionTier => {
    const commissionCurrencies = this.commissionCurrency(commissionTier.currencyValues);
    if (this.props.commissionLevel === commissionLevelConst.PLATFORM) {
      return `${getSymbolFromCurrency(commissionTier.currency) ?? ''}${commissionTier.value}`;
    }

    if (commissionCurrencies.length > 0) {
      const currency = commissionCurrencies.find(item => item.key === this.props.currency);
      if (currency) {
        return `${getSymbolFromCurrency(currency?.key)}${currency?.value}`;
      } else {
        const firstCurrency = this.firstCurrency(commissionCurrencies);
        return `${getSymbolFromCurrency(firstCurrency?.key)}${firstCurrency?.value}`;
      }
    } else {
      return `${getSymbolFromCurrency(this.props.currency) ?? ''}${commissionTier.value}`;
    }
  };

  filterCurrencies = currencies => {
    return currencies.filter(item => item.value);
  };

  shouldDisplayTooltip = commissionTier => {
    const currencies = this.filterCurrencies(
      this.commissionCurrency(commissionTier.currencyValues),
    );
    return (
      commissionTier.triggerPoint === 'CREATED' &&
      commissionTier.currencyValues &&
      currencies.length > 1 &&
      this.props.commissionLevel !== commissionLevelConst.PLATFORM
    );
  };

  render() {
    const { showCard } = this.state;
    const { t, showOperations, showCreateCard, property } = this.props;

    const choiceSvgs = (type, className) => {
      let svgTypes = {
        FLAT_FEE: FlatFee,
        NUM_BOOKINGS: NumBookings,
        TENANCY_LENGTH: TenancyLength,
        REBOOKERS_COMMISSION: RebookersCommission,
      };
      let ResultSvg = svgTypes[type] || 'div';
      return <ResultSvg className={className} />;
    };

    return (
      <div className="commission-list">
        <If
          condition={
            showElementByAuth(platformEntity.COMMISSION_COMISSION_TIERS, entityAction.CREATE) &&
            showCreateCard
          }
        >
          <div
            className={classNames('commission-list__card commission-list__add', {
              'commission-list__second': showCard === 2,
            })}
          >
            <div
              className="commission-list__add-content"
              onClick={() => {
                this.props.openModal('addNew');
              }}
              role="button"
              tabIndex="0"
            >
              <CommissionAdd hash="commission-add" className="commission-list__add-icon" />
              <p>{t('cms.property.commission.create.create_commission_modal.add.title')}</p>
            </div>
          </div>
        </If>
        <For
          index="index"
          of={this.sortCommission(this.props.commissionTier)}
          each="commissionTier"
        >
          <div
            key={index}
            className={classNames('commission-list__card', {
              'commission-list__second': showCard === 2,
            })}
          >
            <div
              className={classNames(
                'commission-list__status',
                {
                  'commission-list__status--active':
                    commissionTier.status === commissionStatus.ACTIVE,
                },
                {
                  'commission-list__status--expire':
                    commissionTier.status === commissionStatus.EXPIRE,
                },
              )}
            >
              <If condition={commissionTier.status}>
                &nbsp;
                {t(`cms.property.commission.list.status.${commissionTier.status.toLowerCase()}`)}
              </If>
            </div>
            <div
              className={classNames('commission-list__icon-box', {
                'commission-list__icon-box--grey':
                  commissionTier.status === commissionStatus.EXPIRE,
              })}
            >
              <If condition={commissionTier.category}>
                {choiceSvgs(
                  commissionTier.category,
                  classNames('commission-list__list-icon', {
                    'commission-list__list-icon--rebookers':
                      commissionTier.category === 'REBOOKERS_COMMISSION',
                  }),
                )}
              </If>
            </div>
            <div className="commission-list__text">
              <div className="commission-list__category">
                <If condition={commissionTier.category}>
                  {t(
                    `cms.property.commission.create.commission_type.${commissionTier.category.toLowerCase()}`,
                  )}
                </If>
              </div>
              <div className="commission-list__great-commission">{commissionTier.name}</div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '8px',
                }}
              >
                {commissionTier.id ? (
                  <div className="commission-list__id">
                    ID: {JSON.parse(window.atob(commissionTier.id))?.id}
                  </div>
                ) : null}
                {commissionTier.triggerPoint && (
                  <div className="commission-list__trigger">
                    {commissionTier.triggerPoint === 'CREATED'
                      ? 'Order/Opp Created'
                      : 'Order/Opp Completed'}
                  </div>
                )}
                {commissionTier.bookingJourney && (
                  <div className="commission-list__trigger">
                    {BookingJourneyStatus[commissionTier.bookingJourney]}
                  </div>
                )}
              </div>

              <div className="commission-list__percentage">
                <If condition={commissionTier.type === 'VALUE'}>
                  {this.displayCurrency(commissionTier)}
                </If>
                <If condition={commissionTier.type === 'PERCENTAGE'}>
                  {`${commissionTier.value}%`}
                </If>
                <If condition={commissionTier.type === 'UNIT_PRICE'}>
                  {`${commissionTier.value}`}
                  <span className="commission-list__unit-price-info">
                    {t('cms.property.commission.list.unit_price_commission')}
                  </span>
                </If>
                {this.shouldDisplayTooltip(commissionTier) && (
                  <Tooltip
                    title={() => (
                      <div>
                        <div>Commission Value</div>
                        {this.filterCurrencies(
                          this.commissionCurrency(commissionTier.currencyValues),
                        )?.map(item => (
                          <div>
                            {item.key} {item.value}
                          </div>
                        ))}
                      </div>
                    )}
                  >
                    <InfoCircleOutlined className="info-icon" style={{ marginLeft: '4px' }} />
                  </Tooltip>
                )}

                <If condition={commissionTier.bonus}>
                  <div className="commission-list__bonus">
                    <div className="commission-list__bonus-text">
                      {t('cms.property.commission.list.bonus')}
                    </div>
                    <CommissionBonus className="commission-list__bonus-icon" />
                  </div>
                </If>
              </div>
              <If condition={commissionTier.category === 'NUM_BOOKINGS'}>
                <div className="commission-list__booking-number">
                  {t('cms.property.commission.list.booking_numbers')}:&nbsp;
                  <If condition={commissionTier.bookingCountTo}>
                    <span>
                      {commissionTier.bookingCountFrom}
                      ~&nbsp;{commissionTier.bookingCountTo}
                    </span>
                  </If>
                  <If condition={!commissionTier.bookingCountTo}>
                    <span>
                      {commissionTier.bookingCountFrom}{' '}
                      {t('cms.property.commission.list.more_than')}
                    </span>
                  </If>
                </div>
              </If>
              <If condition={commissionTier.category === 'TENANCY_LENGTH'}>
                <div className="commission-list__booking-number">
                  {t('cms.property.commission.list.tenancy_length')}:&nbsp;
                  <If condition={commissionTier.tenancyLengthTo}>
                    <span>
                      {`${commissionTier.tenancyLengthFrom} ≤ Length < ${commissionTier.tenancyLengthTo}`}
                      {commissionTier.tenancyUnit
                        ? `(${commissionTier.tenancyUnit.toLowerCase()})`
                        : null}
                    </span>
                  </If>
                  <If condition={!commissionTier.tenancyLengthTo}>
                    <span>
                      {commissionTier.tenancyLengthFrom}&nbsp;
                      {t('cms.property.commission.list.more_than')}&nbsp;&nbsp;
                      {commissionTier.tenancyUnit
                        ? `(${commissionTier.tenancyUnit.toLowerCase()})`
                        : null}
                    </span>
                  </If>
                </div>
              </If>
              <div className="commission-list__effective-dates">
                {`${t('cms.property.commission.list.effective_dates')}: ${formatDay(
                  dayFormatType.UKFORMAT,
                  commissionTier.effectiveFrom,
                )} ~ ${
                  commissionTier.effectiveTo
                    ? formatDay(dayFormatType.UKFORMAT, commissionTier.effectiveTo)
                    : ''
                }`}
              </div>
            </div>
            <If condition={showOperations}>
              <div className="commission-list__bottom">
                <div className="commission-list__btn-box">
                  <If
                    condition={showElementByAuth(
                      platformEntity.COMMISSION_COMISSION_TIERS,
                      entityAction.UPDATE,
                    )}
                  >
                    <button
                      className="listings-tab__edit-listing-btn"
                      onClick={() => {
                        this.props.openModal('edit', commissionTier);
                      }}
                    >
                      <EditOutlined
                        style={{
                          color: '#38b2a6',
                        }}
                      />
                    </button>
                  </If>
                  <If
                    condition={showElementByAuth(
                      platformEntity.COMMISSION_COMISSION_TIERS,
                      entityAction.CREATE,
                    )}
                  >
                    <button
                      className={'listings-tab__copy-listing-btn commission-list__copy'}
                      onClick={() => {
                        this.props.openModal('copy', commissionTier);
                      }}
                    >
                      <CopyOutlined
                        style={{
                          color: '#38b2a6',
                        }}
                      />
                    </button>
                  </If>
                  <If
                    condition={showElementByAuth(
                      platformEntity.COMMISSION_COMISSION_TIERS,
                      entityAction.DELETE,
                    )}
                  >
                    <Choose>
                      <When
                        condition={this.props.commissionLevel === commissionLevelConst.LANDLORD}
                      >
                        <Popconfirm
                          placement="top"
                          title={t('cms.properties.edit.commission.delete_commission_short_hint')}
                          onConfirm={e => {
                            e.stopPropagation();
                            this.handleDeleteCommission(commissionTier.id);
                          }}
                          onCancel={e => {
                            e.stopPropagation();
                          }}
                          okText={t('cms.properties.edit.btn.yes')}
                          okType="danger"
                          cancelText={t('cms.properties.edit.btn.no')}
                        >
                          <button
                            className={'listings-tab__copy-listing-btn commission-list__delete'}
                          >
                            <DeleteOutlined
                              style={{
                                color: '#38b2a6',
                              }}
                            />
                          </button>
                        </Popconfirm>
                      </When>
                      <Otherwise>
                        <PopconfirmWrapper
                          t={t}
                          handleGotoPreview={() => {
                            this.setState({
                              selectedReviewCommission: commissionTier,
                            });
                          }}
                          handleDeleteCommission={() => {
                            this.handleDeleteCommission(commissionTier.id);
                          }}
                        />
                      </Otherwise>
                    </Choose>
                  </If>
                </div>
                <div
                  className="commission-list__update"
                  ref={node => {
                    this.timeDom[index] = node;
                  }}
                >
                  <Tooltip
                    title={this.showUpdate(commissionTier.updatedAt)}
                    getPopupContainer={() => this.timeDom[index]}
                    placement="topRight"
                    arrowPointAtCenter
                  >
                    <Time className="property-card__icon property-card__update-icon" />
                  </Tooltip>
                </div>
              </div>
            </If>
          </div>
        </For>
        <If condition={this.state.selectedReviewCommission}>
          <ReviewCommission
            activeModal
            action={PreCalCommissionWhenPropertyTierChangeAction.DELETE}
            t={t}
            properties={[property]}
            commissionTier={{
              id: this.state.selectedReviewCommission.id,
            }}
            onConfirm={() => {
              this.handleDeleteCommission(this.state.selectedReviewCommission.id);
              this.setState({
                selectedReviewCommission: null,
              });
            }}
            handleCloseModal={() =>
              this.setState({
                selectedReviewCommission: null,
              })
            }
          />
        </If>
      </div>
    );
  }
}

export function PopconfirmWrapper(props) {
  const { t } = props;
  const [showPopconfirm, setShowPopconfirm] = useState(false);
  return (
    <Popconfirm
      visible={showPopconfirm}
      placement="top"
      overlayStyle={{ maxWidth: 240 }}
      overlayClassName="commission-list__popover"
      title={
        <div style={{ maxWidth: 240 }}>
          <p className="commission-list__popover__des">
            {t('cms.properties.edit.commission.delete_commission_hint')}
          </p>
          <p
            className="commission-list__popover__link"
            onClick={() => {
              props.handleGotoPreview();
              setShowPopconfirm(false);
            }}
          >
            {t('cms.properties.edit.commission.preview.popover.link')}
          </p>
        </div>
      }
      onConfirm={e => {
        e.stopPropagation();
        props.handleDeleteCommission();
        setShowPopconfirm(false);
      }}
      onCancel={e => {
        e.stopPropagation();
        setShowPopconfirm(false);
      }}
      okText={t('cms.properties.edit.btn.yes')}
      okType="danger"
      cancelText={t('cms.properties.edit.btn.no')}
    >
      <button
        onClick={() => {
          setShowPopconfirm(true);
        }}
        className={'listings-tab__copy-listing-btn commission-list__delete'}
      >
        <DeleteOutlined
          style={{
            color: '#38b2a6',
          }}
        />
      </button>
    </Popconfirm>
  );
}

CommissionList.propTypes = {
  openModal: PropTypes.func.isRequired,
  commissionTier: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  deleteCommission: PropTypes.func.isRequired,
  showCreateCard: PropTypes.bool,
  commissionLevel: PropTypes.string,
};

CommissionList.defaultProps = {
  openModal: () => {},
  commissionTier: [],
  t: () => {},
  currency: '',
  showCreateCard: true,
  deleteCommission: () => {},
  commissionLevel: '',
};
